/**
 * PMW event bus
 *
 * Native replacement for the jQuery event bus that used to carry all
 * pmw:* events (jQuery(document).on / .trigger). The library core must
 * run without jQuery on any shop platform, so all internal event traffic
 * goes through this emitter.
 *
 * Semantics:
 * - Handlers receive the payload directly: pmw.bus.on("pmw:x", (payload) => {}).
 *   (jQuery handlers received (event, payload); the migration drops the
 *   unused event argument.)
 * - Errors in one handler never break the chain: each handler runs in its
 *   own try/catch and failures are routed to pmw.console.error.
 * - Emission is synchronous, in registration order, like jQuery's.
 *
 * WordPress compatibility: customer snippets documented over the years use
 * jQuery events, both outbound (jQuery(document).on("pmw:event:purchase"))
 * and inbound (jQuery(document).trigger("pmw:add-to-cart", product)). The
 * WordPress platform layer loads the jQuery bridge which mirrors bus
 * events onto jQuery and forwards jQuery-triggered pmw:* events into the
 * bus. The core itself never touches jQuery.
 *
 * @since 1.65.0
 */

(function (pmw) {

	window.pmw = window.pmw || {};

	/** @type {Map<string, Array<Function>>} */
	const listeners = new Map();

	/**
	 * Callbacks the bridge (or tests) can register to observe every emit,
	 * e.g. to mirror events onto jQuery for legacy snippets.
	 *
	 * @type {Array<Function>}
	 */
	const emitObservers = [];

	/**
	 * Replayable events.
	 *
	 * The purchase event fires exactly once per order and is then locked out
	 * permanently: the order ID is written to storage immediately after the
	 * emit, so it never fires again, not even on a page reload. A third-party
	 * integration that registers its handler after an await - loading its own
	 * script, waiting on a CMP - therefore missed the order forever and the
	 * conversion was lost silently.
	 *
	 * For these events the last payload is retained and delivered to handlers
	 * that register after the emit, so registration timing stops mattering.
	 *
	 * Only page-scoped facts belong here. Interaction events (add-to-cart,
	 * select-item) must NOT be replayable: delivering a stale interaction to a
	 * late handler would make integrations report an event that already passed.
	 *
	 * @since 1.65.0
	 * @type {Set<string>}
	 */
	const replayableEvents = new Set([
		"pmw:purchase",
		"pmw:event:purchase",
	]);

	/**
	 * Last payload of each replayable event that has already been emitted.
	 *
	 * @type {Map<string, *>}
	 */
	const replayPayloads = new Map();

	/**
	 * Run a handler without letting it break the caller.
	 *
	 * @param {Function} handler
	 * @param {*} payload
	 * @param {string} context - what to name in the log if it throws
	 * @returns {void}
	 */
	const runHandler = (handler, payload, context) => {
		try {
			handler(payload);
		} catch (e) {
			(pmw.console && pmw.console.error ? pmw.console.error : console.error)(
				`PMW bus: ${context} threw`, e,
			);
		}
	};

	pmw.bus = {

		/**
		 * Register a handler for an event.
		 *
		 * For replayable events (see replayableEvents) that have already been
		 * emitted, the handler receives the retained payload immediately and
		 * synchronously, once. Registering late is therefore safe.
		 *
		 * @param {string} eventName
		 * @param {Function} handler - receives (payload)
		 * @returns {void}
		 */
		on(eventName, handler) {
			if (typeof handler !== "function") return;
			if (!listeners.has(eventName)) listeners.set(eventName, []);
			listeners.get(eventName).push(handler);

			// Replay after registration, so a handler that removes itself
			// (bus.once) or registers further handlers behaves as it would
			// have on the original emit.
			if (replayPayloads.has(eventName)) {
				runHandler(handler, replayPayloads.get(eventName), `replayed handler for ${eventName}`);
			}
		},

		/**
		 * Remove a previously registered handler.
		 *
		 * @param {string} eventName
		 * @param {Function} handler
		 * @returns {void}
		 */
		off(eventName, handler) {
			const handlers = listeners.get(eventName);
			if (!handlers) return;
			const index = handlers.indexOf(handler);
			if (index !== -1) handlers.splice(index, 1);
		},

		/**
		 * Register a handler that runs at most once.
		 *
		 * @param {string} eventName
		 * @param {Function} handler
		 * @returns {void}
		 */
		once(eventName, handler) {
			const wrapper = (payload) => {
				pmw.bus.off(eventName, wrapper);
				handler(payload);
			};
			pmw.bus.on(eventName, wrapper);
		},

		/**
		 * Emit an event to all handlers, synchronously, in registration
		 * order. A throwing handler is logged and skipped, never fatal.
		 *
		 * @param {string} eventName
		 * @param {*} [payload]
		 * @returns {void}
		 */
		emit(eventName, payload) {

			// Retain before dispatch, so a handler that registers another
			// handler for the same replayable event still sees the payload.
			if (replayableEvents.has(eventName)) replayPayloads.set(eventName, payload);

			const handlers = listeners.get(eventName);
			if (handlers) {
				// Copy: handlers may add/remove listeners while running.
				for (const handler of Array.from(handlers)) {
					runHandler(handler, payload, `handler for ${eventName}`);
				}
			}

			for (const observer of emitObservers) {
				try {
					observer(eventName, payload);
				} catch (e) {
					(pmw.console && pmw.console.error ? pmw.console.error : console.error)(
						"PMW bus: emit observer threw", e,
					);
				}
			}
		},

		/**
		 * Observe every emit (used by the WordPress jQuery bridge to mirror
		 * events for legacy snippets, and by tests).
		 *
		 * @param {Function} observer - receives (eventName, payload)
		 * @returns {void}
		 */
		observeEmits(observer) {
			if (typeof observer === "function") emitObservers.push(observer);
		},

		/**
		 * Whether any handler is registered for an event. Lets hot paths
		 * skip payload construction when nobody listens.
		 *
		 * @param {string} eventName
		 * @returns {boolean}
		 */
		hasListeners(eventName) {
			return (listeners.get(eventName) || []).length > 0;
		},
	};

}(window.pmw = window.pmw || {}));
